import { useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { ToolSubmenu } from '../ui/ToolSubmenu';
import { getOrigin, getSectionBounds, getSectionPresets, getSite } from '../../config/siteConfig';
import { downloadSectionExport } from '../../Utils/sectionExport';
import { useSectionStore } from '../../store/sectionStore';
import SectionPlotModal from './SectionPlotModal';
import {
  WIDTH_PRESETS,
  fenceLength,
  formatMine,
  sectionAxisLabel,
  sectionFilename,
  sectionStatusLine,
} from '../../Utils/sectionMath';

const fieldClass =
  'm-0 h-7 rounded-sm border border-yellow-600/30 bg-zinc-950 px-1.5 text-[10px] text-yellow-100 focus:border-yellow-400 focus:outline-none';

const Chip = ({ active, onClick, children, title, layout }) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    className={`m-0 h-7 px-1.5 shrink-0 rounded-sm border text-[10px] font-semibold uppercase tracking-wider ${
      layout === 'column' ? 'flex-1 min-w-0' : ''
    } ${
      active
        ? 'border-yellow-400 bg-yellow-500 text-black'
        : 'border-yellow-600/30 bg-zinc-950 text-yellow-400 hover:border-yellow-400'
    }`}
  >
    {children}
  </button>
);

/**
 * Scene clip / section controls — row under top bar, or column on the right.
 */
export const SceneSlicer = ({ three, layout = 'row', onClose }) => {
  const panelOpen = useSectionStore((s) => s.panelOpen);
  const [plotOpen, setPlotOpen] = useState(false);
  const s = useSectionStore(
    useShallow((state) => ({
      enabled: state.enabled,
      orientation: state.orientation,
      centerEast: state.centerEast,
      centerNorth: state.centerNorth,
      centerRl: state.centerRl,
      width: state.width,
      azimuth: state.azimuth,
      clipMode: state.clipMode,
      lookLocked: state.lookLocked,
      lookFlip: state.lookFlip,
      boxWidthEast: state.boxWidthEast,
      boxWidthNorth: state.boxWidthNorth,
      boxWidthRl: state.boxWidthRl,
      clipHoles: state.clipHoles,
      clipSolids: state.clipSolids,
      showCaps: state.showCaps,
      showGizmo: state.showGizmo,
      stepSize: state.stepSize,
      placeMode: state.placeMode,
      pendingPoints: state.pendingPoints,
      fencePoints: state.fencePoints,
      fenceDistance: state.fenceDistance,
      clipParentUuid: state.clipParentUuid,
      clipParentName: state.clipParentName,
      clipParentMode: state.clipParentMode,
      clipParentClosed: state.clipParentClosed,
      clipParentUsedBox: state.clipParentUsedBox,
    }))
  );

  const presets = useMemo(() => getSectionPresets(), []);
  const bounds = useMemo(() => getSectionBounds(), []);
  const origin = getOrigin();

  if (!panelOpen) return <SectionPlotModal open={plotOpen} onClose={() => setPlotOpen(false)} />;

  const col = layout === 'column';
  const labelClass = col
    ? 'flex flex-col items-stretch gap-0.5 w-full text-[9px] uppercase tracking-wider text-zinc-500'
    : 'inline-flex items-center gap-1 shrink-0 text-[9px] uppercase tracking-wider text-zinc-500';
  const chipRow = col ? 'flex w-full gap-1' : 'inline-flex items-center gap-1 shrink-0';

  const activeAxis = s.orientation === 'east' || s.orientation === 'box'
    ? 'east'
    : s.orientation === 'north'
      ? 'north'
      : s.orientation === 'rl'
        ? 'rl'
        : s.orientation === 'fence'
          ? 'fence'
          : 'east';

  const slider = (() => {
    if (activeAxis === 'fence') {
      const max = Math.max(0, fenceLength(s.fencePoints));
      return {
        min: 0,
        max,
        value: s.fenceDistance,
        onChange: (v) => useSectionStore.setState({ fenceDistance: Number(v) || 0 }),
      };
    }
    if (activeAxis === 'north') {
      return {
        min: bounds.north[0],
        max: bounds.north[1],
        value: s.centerNorth,
        onChange: (v) => useSectionStore.getState().setCenter({ north: Number(v) }),
      };
    }
    if (activeAxis === 'rl') {
      return {
        min: bounds.rl[0],
        max: bounds.rl[1],
        value: s.centerRl,
        onChange: (v) => useSectionStore.getState().setCenter({ rl: Number(v) }),
      };
    }
    return {
      min: bounds.east[0],
      max: bounds.east[1],
      value: s.centerEast,
      onChange: (v) => useSectionStore.getState().setCenter({ east: Number(v) }),
    };
  })();

  const placeHint =
    s.placeMode === 'click'
      ? 'Click a point in the scene'
      : s.placeMode === 'twoPoint'
        ? `Click two points (${s.pendingPoints.length}/2)`
        : s.placeMode === 'threePoint'
          ? `Click three points (${s.pendingPoints.length}/3)`
          : s.placeMode === 'fence'
            ? `Click fence vertices (${s.pendingPoints.length})`
            : s.placeMode === 'object'
              ? 'Click a solid or surface to clip against'
              : null;

  const jumpOptions = (() => {
    if (s.orientation === 'east' || s.orientation === 'azimuth') return presets.zones || [];
    if (s.orientation === 'north') return presets.northings || [];
    if (s.orientation === 'rl' || s.orientation === 'box') return presets.benches || [];
    return [];
  })();

  const closePanel = () => {
    useSectionStore.getState().setPanelOpen(false);
    onClose?.();
  };

  return (
    <>
    <ToolSubmenu layout={layout} label="Section" onClose={closePanel}>
      <div className={chipRow}>
        <Chip layout={layout} active={s.enabled} onClick={() => useSectionStore.getState().setEnabled(!s.enabled)} title="Clip on/off">
          {s.enabled ? 'Clip on' : 'Clip off'}
        </Chip>
      </div>
      <p className="m-0 text-[10px] text-yellow-200/90 tabular-nums leading-tight">
        E {formatMine(s.centerEast, 0)} · RL {formatMine(s.centerRl, 0)} · N {formatMine(s.centerNorth, 0)}
      </p>
      <p className="m-0 text-[9px] text-zinc-500 -mt-1">{sectionStatusLine(s)}</p>

      <span className="text-[9px] uppercase tracking-wider text-zinc-500">Orientation</span>
      <div className={chipRow}>
        <Chip layout={layout} active={s.orientation === 'east'} onClick={() => useSectionStore.getState().setOrientation('east')} title="N–S face, constant easting">
          East
        </Chip>
        <Chip layout={layout} active={s.orientation === 'north'} onClick={() => useSectionStore.getState().setOrientation('north')} title="E–W face, constant northing">
          North
        </Chip>
        <Chip layout={layout} active={s.orientation === 'rl'} onClick={() => useSectionStore.getState().setOrientation('rl')} title="Plan slice">
          RL
        </Chip>
      </div>
      <div className={chipRow}>
        <Chip layout={layout} active={s.orientation === 'azimuth'} onClick={() => useSectionStore.getState().setOrientation('azimuth')} title="Vertical section by azimuth">
          Azimuth
        </Chip>
        <Chip layout={layout} active={s.orientation === 'box'} onClick={() => useSectionStore.getState().setOrientation('box')} title="Clip a room">
          Box
        </Chip>
        <Chip layout={layout} active={s.orientation === 'oblique'} onClick={() => useSectionStore.getState().setOrientation('oblique')} title="Three-point plane">
          Oblique
        </Chip>
      </div>
      <div className={chipRow}>
        <Chip layout={layout} active={s.orientation === 'fence'} onClick={() => useSectionStore.getState().setOrientation('fence')} title="Step along a polyline">
          Fence
        </Chip>
        <Chip layout={layout} active={s.orientation === 'object'} onClick={() => useSectionStore.getState().setOrientation('object')} title="Clip inside, outside, above or below a parent object">
          Object
        </Chip>
      </div>
      {s.orientation !== 'object' && (
      <div className={chipRow}>
        <Chip layout={layout} active={s.clipMode === 'corridor'} onClick={() => useSectionStore.getState().setClipMode('corridor')} title="Keep a corridor">
          Corridor
        </Chip>
        <Chip layout={layout} active={s.clipMode === 'single'} onClick={() => useSectionStore.getState().setClipMode('single')} title="Single cut plane">
          Cut
        </Chip>
      </div>
      )}

      {s.orientation === 'azimuth' && (
        <label className={labelClass}>
          Azimuth °
          <input
            className={`${fieldClass} ${col ? 'w-full' : 'w-16'}`}
            type="number"
            step="1"
            value={Math.round(s.azimuth)}
            onChange={(e) => useSectionStore.getState().setAzimuth(e.target.value)}
          />
        </label>
      )}

      {s.orientation !== 'box' && s.orientation !== 'object' && (
        <label className={labelClass}>
          Width m
          <input
            className={`${fieldClass} ${col ? 'w-full' : 'w-16'}`}
            type="number"
            min="0.1"
            step="0.5"
            value={s.width}
            onChange={(e) => useSectionStore.getState().setWidth(e.target.value)}
          />
        </label>
      )}
      {s.orientation !== 'box' && s.orientation !== 'object' && (
        <div className={chipRow}>
          {WIDTH_PRESETS.map((w) => (
            <Chip key={w} layout={layout} active={Number(s.width) === w} onClick={() => useSectionStore.getState().setWidth(w)}>
              {w}
            </Chip>
          ))}
        </div>
      )}
      {s.orientation !== 'box' && s.orientation !== 'object' && s.clipMode === 'corridor' && (
        <input
          className="w-full accent-yellow-500"
          type="range"
          min="0.1"
          max="100"
          step="0.5"
          value={Math.min(100, s.width)}
          onChange={(e) => useSectionStore.getState().setWidth(e.target.value)}
        />
      )}

      {s.orientation === 'box' && (
        <>
          <label className={labelClass}>
            Box E × N × RL
            <div className={col ? 'grid grid-cols-3 gap-1' : 'inline-flex gap-1'}>
              <input className={fieldClass} type="number" min="0.1" value={s.boxWidthEast} onChange={(e) => useSectionStore.getState().setBoxWidth({ east: e.target.value })} title="Easting width" />
              <input className={fieldClass} type="number" min="0.1" value={s.boxWidthNorth} onChange={(e) => useSectionStore.getState().setBoxWidth({ north: e.target.value })} title="Northing width" />
              <input className={fieldClass} type="number" min="0.1" value={s.boxWidthRl} onChange={(e) => useSectionStore.getState().setBoxWidth({ rl: e.target.value })} title="RL thickness" />
            </div>
          </label>
        </>
      )}

      {s.orientation === 'object' || s.clipParentUuid ? (
        <>
          <span className="text-[9px] uppercase tracking-wider text-zinc-500">Object clip</span>
          <div className={chipRow}>
            <Chip layout={layout} active={s.clipParentMode === 'inside'} onClick={() => useSectionStore.getState().setClipParentMode('inside')} title="Keep geometry inside the object. Open meshes use the bounding box.">
              Inside
            </Chip>
            <Chip layout={layout} active={s.clipParentMode === 'outside'} onClick={() => useSectionStore.getState().setClipParentMode('outside')} title="Keep geometry outside the object. Open meshes use the bounding box.">
              Outside
            </Chip>
          </div>
          <div className={chipRow}>
            <Chip layout={layout} active={s.clipParentMode === 'above'} onClick={() => useSectionStore.getState().setClipParentMode('above')} title="Treat the object as a surface; keep what is above it">
              Above
            </Chip>
            <Chip layout={layout} active={s.clipParentMode === 'below'} onClick={() => useSectionStore.getState().setClipParentMode('below')} title="Treat the object as a surface; keep what is below it">
              Below
            </Chip>
          </div>
          {s.clipParentUuid ? (
            <p className="m-0 text-[10px] text-yellow-200/90 leading-tight">
              {s.clipParentName || 'Object'}
              {s.clipParentMode === 'above' || s.clipParentMode === 'below'
                ? ' · surface'
                : s.clipParentClosed
                  ? ' · closed mesh'
                  : ' · bounding box'}
            </p>
          ) : (
            <p className="m-0 text-[10px] text-yellow-400/90 leading-tight">Click Pick object, then a solid or surface</p>
          )}
          {s.clipParentUuid ? (
            <div className={chipRow}>
              <Chip layout={layout} title="Stop clipping against this object" onClick={() => useSectionStore.getState().clearClipParent()}>
                Clear object
              </Chip>
            </div>
          ) : null}
        </>
      ) : null}

      {s.orientation !== 'object' && (
        <>
      <label className={labelClass}>
        Centre
        <div className={col ? 'grid grid-cols-3 gap-1' : 'inline-flex gap-1'}>
          <input className={fieldClass} type="number" step="1" value={Number(s.centerEast.toFixed(1))} onChange={(e) => useSectionStore.getState().setCenter({ east: e.target.value })} title="Easting" />
          <input className={fieldClass} type="number" step="1" value={Number(s.centerRl.toFixed(1))} onChange={(e) => useSectionStore.getState().setCenter({ rl: e.target.value })} title="RL" />
          <input className={fieldClass} type="number" step="1" value={Number(s.centerNorth.toFixed(1))} onChange={(e) => useSectionStore.getState().setCenter({ north: e.target.value })} title="Northing" />
        </div>
      </label>

      <label className={labelClass}>
        {activeAxis === 'fence' ? 'Along fence' : `Scrub ${sectionAxisLabel({ orientation: activeAxis === 'fence' ? 'fence' : activeAxis === 'rl' ? 'rl' : activeAxis === 'north' ? 'north' : 'east' })}`}
        <input
          className="w-full accent-yellow-500"
          type="range"
          min={slider.min}
          max={slider.max}
          step={activeAxis === 'rl' ? 1 : 5}
          value={slider.value}
          onChange={(e) => slider.onChange(e.target.value)}
        />
      </label>
        </>
      )}

      {jumpOptions.length > 0 && (
        <label className={labelClass}>
          Jump
          <select
            className={`${fieldClass} ${col ? 'w-full' : ''}`}
            defaultValue=""
            onChange={(e) => {
              const idx = Number(e.target.value);
              const row = jumpOptions[idx];
              if (!row) return;
              if (row.east != null) useSectionStore.getState().setCenter({ east: row.east });
              if (row.north != null) useSectionStore.getState().setCenter({ north: row.north });
              if (row.rl != null) useSectionStore.getState().setCenter({ rl: row.rl });
              e.target.value = '';
            }}
          >
            <option value="" disabled>
              {s.orientation === 'rl' ? 'Level / bench…' : 'Named location…'}
            </option>
            {jumpOptions.map((row, i) => (
              <option key={`${row.name}-${i}`} value={i}>
                {row.name}
                {row.rl != null ? ` — ${Math.round(row.rl)}` : ''}
                {row.east != null ? ` — ${Math.round(row.east)}` : ''}
                {row.north != null ? ` — ${Math.round(row.north)}` : ''}
              </option>
            ))}
          </select>
        </label>
      )}

      <span className="text-[9px] uppercase tracking-wider text-zinc-500">Place</span>
      <div className={chipRow}>
        <Chip layout={layout} active={s.placeMode === 'click'} onClick={() => useSectionStore.getState().setPlaceMode(s.placeMode === 'click' ? 'idle' : 'click')} title="Click to set centre">
          Click
        </Chip>
        <Chip layout={layout} active={s.placeMode === 'twoPoint'} onClick={() => useSectionStore.getState().setPlaceMode(s.placeMode === 'twoPoint' ? 'idle' : 'twoPoint')} title="Vertical plane through two points">
          2-pt
        </Chip>
        <Chip layout={layout} active={s.placeMode === 'threePoint'} onClick={() => useSectionStore.getState().setPlaceMode(s.placeMode === 'threePoint' ? 'idle' : 'threePoint')} title="Plane through three points">
          3-pt
        </Chip>
        <Chip layout={layout} active={s.placeMode === 'fence'} onClick={() => useSectionStore.getState().setPlaceMode(s.placeMode === 'fence' ? 'idle' : 'fence')} title="Polyline to step along">
          Fence
        </Chip>
        <Chip layout={layout} active={s.placeMode === 'object'} onClick={() => useSectionStore.getState().setPlaceMode(s.placeMode === 'object' ? 'idle' : 'object')} title="Click a solid or surface to clip against">
          Object
        </Chip>
      </div>
      {placeHint && <p className="m-0 text-[10px] text-yellow-400/90 leading-tight">{placeHint}</p>}
      {(s.placeMode === 'fence' && s.pendingPoints.length >= 2) ||
      (s.pendingPoints.length > 0 || (s.fencePoints || []).length > 0) ? (
        <div className={chipRow}>
          {s.placeMode === 'fence' && s.pendingPoints.length >= 2 ? (
            <Chip layout={layout} active onClick={() => useSectionStore.getState().finishFence()}>
              Done fence
            </Chip>
          ) : null}
          {s.pendingPoints.length > 0 || (s.fencePoints || []).length > 0 ? (
            <Chip
              layout={layout}
              title="Remove pick points and fence lines. The slice stays on."
              onClick={() => useSectionStore.getState().clearPoints()}
            >
              Clear points
            </Chip>
          ) : null}
        </div>
      ) : null}

      {s.orientation !== 'object' && (
      <div className={chipRow}>
        <Chip layout={layout} active={s.lookLocked} onClick={() => useSectionStore.getState().toggleLookLocked()} title="Look perpendicular to the section">
          Look
        </Chip>
        <Chip layout={layout} active={s.lookFlip} onClick={() => useSectionStore.getState().toggleLookFlip()} title="Other side of the plane">
          Flip
        </Chip>
      </div>
      )}

      {s.orientation !== 'object' && (
      <label className={labelClass}>
        Step m
        <div className={col ? 'flex gap-1' : 'inline-flex gap-1'}>
          <button type="button" className={`${fieldClass} px-2`} onClick={() => useSectionStore.getState().step(-1)} title="Step back ([)">
            −
          </button>
          <input
            className={`${fieldClass} ${col ? 'flex-1' : 'w-14'}`}
            type="number"
            min="0.1"
            step="1"
            value={s.stepSize}
            onChange={(e) => useSectionStore.getState().setStepSize(e.target.value)}
          />
          <button type="button" className={`${fieldClass} px-2`} onClick={() => useSectionStore.getState().step(1)} title="Step forward (])">
            +
          </button>
        </div>
      </label>
      )}

      <div className={chipRow}>
        <Chip layout={layout} active={s.showGizmo} onClick={() => useSectionStore.getState().setShowGizmo(!s.showGizmo)}>
          Plane
        </Chip>
        <Chip layout={layout} active={s.showCaps} onClick={() => useSectionStore.getState().setShowCaps(!s.showCaps)}>
          Caps
        </Chip>
      </div>
      <div className={chipRow}>
        <Chip layout={layout} active={s.clipSolids} onClick={() => useSectionStore.getState().setClipSolids(!s.clipSolids)} title="Clip solids / GLBs">
          Solids
        </Chip>
        <Chip layout={layout} active={s.clipHoles} onClick={() => useSectionStore.getState().setClipHoles(!s.clipHoles)} title="Clip drillholes">
          Holes
        </Chip>
      </div>

      <button
        type="button"
        className={`m-0 h-7 px-2 shrink-0 rounded-sm border border-yellow-600/40 bg-yellow-500 text-[10px] font-semibold uppercase tracking-wider text-black hover:bg-yellow-400 ${
          col ? 'w-full' : ''
        }`}
        onClick={async () => {
          try {
            const projectName = getSite().projectName;
            await downloadSectionExport(three?.gl, three?.camera, three?.controlRef?.current, {
              scene: three?.scene,
              state: useSectionStore.getState(),
              origin,
              projectName,
              title: projectName || 'Section',
              filename: sectionFilename(useSectionStore.getState(), projectName),
            });
          } catch (e) {
            console.warn('[section export]', e?.message || e);
          }
        }}
      >
        Export PNG
      </button>
      <button
        type="button"
        className={`m-0 h-7 px-2 shrink-0 rounded-sm border border-yellow-600/40 text-[10px] font-semibold uppercase tracking-wider text-yellow-400 hover:bg-yellow-500/10 ${
          col ? 'w-full' : ''
        }`}
        title="2D plot: holes on the plane, PDF, tonnes in clip"
        onClick={() => setPlotOpen(true)}
      >
        Plot 2D
      </button>
      <SectionPlotModal open={plotOpen} onClose={() => setPlotOpen(false)} />
    </ToolSubmenu>
    </>
  );
};
